import React, { useState } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import type { StackNavigationProp } from '@react-navigation/stack';
import type { RootStackParamList } from '../types/navigation';
import { Icon } from '../components/Icon';
import { colors, spacing, fontWeights, borderRadius } from '../theme';

type NavigationProp = StackNavigationProp<RootStackParamList, 'M07ResultScreen'>;

export const M07ResultScreen: React.FC = () => {
  const navigation = useNavigation<NavigationProp>();
  const [showFullTable, setShowFullTable] = useState(false);

  return (
    <View style={styles.container}>
      <ScrollView contentContainerStyle={styles.scrollContent}>
        {/* Client Header Card */}
        <View style={styles.clientCard}>
          <Icon name="person" size={24} color={colors.primary} />
          <View>
            <Text style={styles.clientName}>Nguyễn Văn Minh - 35t Nam</Text>
            <Text style={styles.clientProduct}>Sản phẩm: ManulifeMOVE - An Phát Đầu Tư</Text>
          </View>
        </View>

        {/* Tóm tắt metrics */}
        <View style={styles.card}>
          <Text style={styles.cardTitle}>📋 Tóm tắt quyền lợi & đóng phí</Text>
          
          <View style={styles.metricsGrid}>
            <View style={styles.metricItem}>
              <Text style={styles.metricLabel}>💰 Tổng phí đóng</Text>
              <Text style={styles.metricValue}>600M VNĐ</Text>
              <Text style={styles.metricSub}>20 năm định kỳ</Text>
            </View>
            <View style={styles.metricItem}>
              <Text style={styles.metricLabel}>🛡 Số tiền bảo hiểm</Text>
              <Text style={styles.metricValue}>1 Tỷ VNĐ</Text>
              <Text style={styles.metricSub}>Bảo vệ sinh mạng</Text>
            </View>
            <View style={styles.metricItem}>
              <Text style={styles.metricLabel}>📈 Giá trị TK năm 20</Text>
              <Text style={styles.metricValue}>850M VNĐ</Text>
              <Text style={styles.metricSub}>Giả định lãi suất 7%</Text>
            </View>
            <View style={styles.metricItem}>
              <Text style={styles.metricLabel}>💵 Nhận đáo hạn</Text>
              <Text style={styles.metricValue}>850M VNĐ</Text>
              <Text style={styles.metricSub}>Hoàn lại ước tính</Text>
            </View>
          </View>

          <View style={styles.detailsList}>
            <Text style={styles.detailListItem}>• Phí đóng mỗi năm: 30.000.000 VNĐ</Text>
            <Text style={styles.detailListItem}>• Lãi suất giả định trung bình: 7%/năm</Text>
            <Text style={styles.detailListItem}>• Phí bảo hiểm rủi ro TB: 2.500.000 VNĐ/năm</Text>
          </View>
        </View>

        {/* Mock Biểu đồ dòng tiền */}
        <View style={styles.card}>
          <Text style={styles.cardTitle}>📊 Biểu đồ dòng tiền (Giả định 7%/năm)</Text>
          
          {/* Visual mock chart */}
          <View style={styles.chartMock}>
            {/* Y axis labels */}
            <View style={styles.chartYAxis}>
              <Text style={styles.chartAxisText}>1 tỷ</Text>
              <Text style={styles.chartAxisText}>600M</Text>
              <Text style={styles.chartAxisText}>300M</Text>
              <Text style={styles.chartAxisText}>0</Text>
            </View>
            {/* Chart Area */}
            <View style={styles.chartMain}>
              {/* Lines & curves mocked via CSS views */}
              <View style={styles.chartLinePremium} />
              <View style={styles.chartLineValue} />
              
              {/* Vertical grids */}
              <View style={styles.chartGridRow}>
                <View style={styles.chartGridCol} />
                <View style={styles.chartGridCol} />
                <View style={styles.chartGridCol} />
                <View style={styles.chartGridCol} />
                <View style={styles.chartGridCol} />
              </View>
            </View>
          </View>
          
          {/* X axis labels */}
          <View style={styles.chartXAxis}>
            <Text style={styles.chartAxisText}>Năm 1</Text>
            <Text style={styles.chartAxisText}>Năm 5</Text>
            <Text style={styles.chartAxisText}>Năm 10</Text>
            <Text style={styles.chartAxisText}>Năm 15</Text>
            <Text style={styles.chartAxisText}>Năm 20</Text>
          </View>

          {/* Legends */}
          <View style={styles.legendsRow}>
            <View style={styles.legend}>
              <View style={[styles.legendColor, { backgroundColor: '#FF6B00' }]} />
              <Text style={styles.legendText}>Tổng phí đóng</Text>
            </View>
            <View style={styles.legend}>
              <View style={[styles.legendColor, { backgroundColor: '#003366' }]} />
              <Text style={styles.legendText}>Giá trị tài khoản</Text>
            </View>
          </View>
        </View>

        {/* Bảng dòng tiền */}
        <View style={styles.card}>
          <Text style={styles.cardTitle}>📋 Dòng tiền chi tiết theo năm</Text>
          
          <View style={styles.table}>
            <View style={styles.tableHeader}>
              <Text style={[styles.tableCell, styles.tableHeaderCell, { width: 50 }]}>Năm</Text>
              <Text style={[styles.tableCell, styles.tableHeaderCell]}>Phí đóng</Text>
              <Text style={[styles.tableCell, styles.tableHeaderCell]}>Phí BH</Text>
              <Text style={[styles.tableCell, styles.tableHeaderCell]}>Giá trị TK</Text>
              <Text style={[styles.tableCell, styles.tableHeaderCell]}>Hoàn lại</Text>
            </View>
            
            <View style={styles.tableRow}>
              <Text style={[styles.tableCell, { width: 50 }]}>1</Text>
              <Text style={styles.tableCell}>30M</Text>
              <Text style={styles.tableCell}>1.2M</Text>
              <Text style={styles.tableCell}>26M</Text>
              <Text style={styles.tableCell}>24M</Text>
            </View>
            <View style={styles.tableRow}>
              <Text style={[styles.tableCell, { width: 50 }]}>2</Text>
              <Text style={styles.tableCell}>30M</Text>
              <Text style={styles.tableCell}>1.4M</Text>
              <Text style={styles.tableCell}>53M</Text>
              <Text style={styles.tableCell}>51M</Text>
            </View>
            <View style={styles.tableRow}>
              <Text style={[styles.tableCell, { width: 50 }]}>3</Text>
              <Text style={styles.tableCell}>30M</Text>
              <Text style={styles.tableCell}>1.6M</Text>
              <Text style={styles.tableCell}>83M</Text>
              <Text style={styles.tableCell}>80M</Text>
            </View>

            {showFullTable && (
              <>
                <View style={styles.tableRow}>
                  <Text style={[styles.tableCell, { width: 50 }]}>10</Text>
                  <Text style={styles.tableCell}>30M</Text>
                  <Text style={styles.tableCell}>2.8M</Text>
                  <Text style={styles.tableCell}>350M</Text>
                  <Text style={styles.tableCell}>345M</Text>
                </View>
                <View style={styles.tableRow}>
                  <Text style={[styles.tableCell, { width: 50 }]}>20</Text>
                  <Text style={styles.tableCell}>30M</Text>
                  <Text style={styles.tableCell}>5.0M</Text>
                  <Text style={styles.tableCell}>850M</Text>
                  <Text style={styles.tableCell}>850M</Text>
                </View>
              </>
            )}
          </View>
          
          <TouchableOpacity style={styles.tableToggle} onPress={() => setShowFullTable(!showFullTable)}>
            <Text style={styles.tableToggleText}>{showFullTable ? 'Thu gọn bảng' : 'Xem bảng đầy đủ →'}</Text>
          </TouchableOpacity>
        </View>

        {/* Action Buttons */}
        <View style={styles.actionsContainer}>
          <TouchableOpacity style={styles.actionBtnOutline} onPress={() => navigation.navigate('M09PdfScreen')}>
            <Icon name="picture_as_pdf" size={20} color={colors.primary} />
            <Text style={styles.actionBtnTextOutline}>Xuất PDF</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.actionBtnOutline}>
            <Icon name="mail" size={20} color={colors.primary} />
            <Text style={styles.actionBtnTextOutline}>Gửi email</Text>
          </TouchableOpacity>
        </View>

        <TouchableOpacity style={styles.actionBtnSolid} onPress={() => navigation.navigate('M08CompareScreen')}>
          <Icon name="compare_arrows" size={20} color="#ffffff" />
          <Text style={styles.actionBtnTextSolid}>So sánh phương án</Text>
        </TouchableOpacity>

        <TouchableOpacity style={styles.actionBtnOutlineFull} onPress={() => navigation.navigate('M10SaveCustomerScreen')}>
          <Icon name="save" size={20} color={colors.primary} />
          <Text style={styles.actionBtnTextOutline}>Lưu vào KH của tôi</Text>
        </TouchableOpacity>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f7fa',
  },
  scrollContent: {
    padding: spacing[4],
    paddingBottom: 40,
    gap: spacing[4],
  },
  clientCard: {
    backgroundColor: '#ffffff',
    padding: spacing[4],
    borderRadius: borderRadius.md,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[3],
    borderWidth: 1,
    borderColor: colors.outlineVariant,
  },
  clientName: {
    fontSize: 15,
    fontWeight: fontWeights.bold,
    color: colors.onSurface,
  },
  clientProduct: {
    fontSize: 12.5,
    color: colors.onSurfaceVariant,
    marginTop: spacing[1],
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: borderRadius.md,
    padding: spacing[4],
    borderWidth: 1,
    borderColor: colors.outlineVariant,
  },
  cardTitle: {
    fontSize: 15,
    fontWeight: fontWeights.bold,
    color: colors.onSurface,
    marginBottom: spacing[3],
  },
  metricsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing[3],
  },
  metricItem: {
    width: '47%',
    backgroundColor: colors.surface,
    padding: spacing[3],
    borderRadius: borderRadius.DEFAULT,
    gap: spacing[1],
  },
  metricLabel: {
    fontSize: 11,
    fontWeight: fontWeights.semibold,
    color: colors.onSurfaceVariant,
  },
  metricValue: {
    fontSize: 18,
    fontWeight: fontWeights.bold,
    color: colors.primary,
  },
  metricSub: {
    fontSize: 10,
    color: colors.onSurfaceVariant,
  },
  detailsList: {
    marginTop: spacing[4],
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: colors.outlineVariant,
    paddingTop: spacing[3],
    gap: spacing[1.5],
  },
  detailListItem: {
    fontSize: 12,
    color: colors.onSurfaceVariant,
  },
  chartMock: {
    height: 180,
    flexDirection: 'row',
    gap: spacing[2],
    alignItems: 'stretch',
    marginTop: spacing[2],
  },
  chartYAxis: {
    justifyContent: 'space-between',
    paddingVertical: spacing[1],
  },
  chartAxisText: {
    fontSize: 10,
    color: colors.onSurfaceVariant,
  },
  chartMain: {
    flex: 1,
    backgroundColor: '#fafafa',
    borderWidth: 1,
    borderColor: colors.outlineVariant,
    borderRadius: borderRadius.sm,
    position: 'relative',
    overflow: 'hidden',
  },
  chartLinePremium: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: '40%',
    height: 2,
    backgroundColor: '#FF6B00',
  },
  chartLineValue: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 20,
    height: 100,
    borderBottomWidth: 2,
    borderBottomColor: '#003366',
    borderStyle: 'solid',
    transform: [{ rotate: '-15deg' }, { translateY: -40 }],
  },
  chartGridRow: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-around',
  },
  chartGridCol: {
    width: StyleSheet.hairlineWidth,
    backgroundColor: 'rgba(0,0,0,0.05)',
  },
  chartXAxis: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingLeft: 40,
    marginTop: spacing[2],
  },
  legendsRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: spacing[4],
    marginTop: spacing[3],
  },
  legend: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[1.5],
  },
  legendColor: {
    width: 12,
    height: 4,
    borderRadius: 2,
  },
  legendText: {
    fontSize: 11,
    color: colors.onSurface,
  },
  table: {
    borderWidth: 1,
    borderColor: colors.outlineVariant,
    borderRadius: borderRadius.sm,
    overflow: 'hidden',
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: colors.surface,
    borderBottomWidth: 1,
    borderBottomColor: colors.outlineVariant,
    paddingVertical: spacing[2],
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.outlineVariant,
    paddingVertical: spacing[2],
  },
  tableCell: {
    flex: 1,
    fontSize: 12,
    textAlign: 'center',
    color: colors.onSurface,
  },
  tableHeaderCell: {
    fontWeight: fontWeights.bold,
    color: colors.primary,
  },
  tableToggle: {
    alignItems: 'center',
    paddingVertical: spacing[3],
  },
  tableToggleText: {
    fontSize: 13,
    fontWeight: fontWeights.semibold,
    color: colors.secondary,
  },
  actionsContainer: {
    flexDirection: 'row',
    gap: spacing[3],
  },
  actionBtnOutline: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: colors.primary,
    borderRadius: borderRadius.DEFAULT,
    height: 48,
    gap: spacing[2],
    backgroundColor: '#ffffff',
  },
  actionBtnOutlineFull: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: colors.primary,
    borderRadius: borderRadius.DEFAULT,
    height: 48,
    gap: spacing[2],
    backgroundColor: '#ffffff',
  },
  actionBtnTextOutline: {
    fontSize: 14,
    fontWeight: fontWeights.bold,
    color: colors.primary,
  },
  actionBtnSolid: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.primary,
    borderRadius: borderRadius.DEFAULT,
    height: 48,
    gap: spacing[2],
  },
  actionBtnTextSolid: {
    fontSize: 14,
    fontWeight: fontWeights.bold,
    color: '#ffffff',
  },
});
